/**
 * PlatformerPreloader — per-stage asset preloading + the loading gate.
 *
 * Scans a stage JSON for every asset it can possibly draw or play:
 *
 *   - tile sprites (incl. autotile template atlases) and decor,
 *   - entity sprites (enemies, items, notes, platforms, …),
 *   - the background + parallax layer slots,
 *   - fx.emitter PNG-particle textures,
 *   - note / sound-zone / music-zone audio clips,
 *
 * then loads them BEFORE the board mounts: textures decode into the
 * SkinResolver's shared cache (so `createSprite` applies them
 * synchronously — no pop-in on frame one), audio decodes into the
 * NoteAudioPlayer that the scene then hands to PlatformerNotes (so the
 * first note contact never stutters on fetch+decode).
 *
 * Everything is best-effort: a missing/broken asset resolves null and
 * the stage mounts with its procedural fallback, exactly as before.
 * Slots with no imported art are skipped instantly (no work, no wait),
 * so stages built purely from placeholder art gate for ~0 ms.
 */

import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import { PLATFORMER_TILE_DEFS, PLATFORMER_ENTITY_DEFS } from './platformerConfig.js';

/**
 * Walk a stage JSON and collect every referenced asset key.
 * @returns {{ textureSlots: Set<string>, audioKeys: Set<string> }}
 */
export function collectStageAssets(stage) {
    const textureSlots = new Set();
    const audioKeys = new Set();
    if (!stage) return { textureSlots, audioKeys };

    // Backdrop + stacked parallax layers.
    textureSlots.add('platformer.background');
    for (const bg of stage.backgroundLayers || []) {
        if (bg?.slot) textureSlots.add(bg.slot);
    }

    for (const layer of stage.layers || []) {
        for (const tile of layer.tiles || []) {
            const def = PLATFORMER_TILE_DEFS[tile?.type];
            if (!def) continue;
            if (def.slot) textureSlots.add(def.slot);
            // Autotiled terrain renders from its template atlas instead
            // of (or in addition to) the plain slot.
            if (def.autotile?.slot) textureSlots.add(def.autotile.slot);
        }
        for (const ent of layer.entities || []) {
            const def = PLATFORMER_ENTITY_DEFS[ent?.type];
            if (def?.slot) textureSlots.add(def.slot);
            // fx.emitter PNG-particle texture (a slot key in props).
            if (ent?.props?.texture) textureSlots.add(ent.props.texture);
            // Music notes + sound/music area zones (slot key in props).
            if (ent?.props?.audio) audioKeys.add(ent.props.audio);
        }
    }
    return { textureSlots, audioKeys };
}

/**
 * Preload every asset a stage references.
 *
 * @param {object} stage   stage JSON
 * @param {import('../../core/SkinResolver.js').SkinResolver} theme
 * @param {{ audio?: import('../../core/NoteAudioPlayer.js').NoteAudioPlayer,
 *           onProgress?: (frac: number) => void }} [opts]
 *        `audio` — player to decode clips into (pass the instance the
 *        scene will hand to PlatformerNotes so the decode is reused);
 *        omit to skip audio. `onProgress` — 0..1 completion callback.
 */
export async function preloadStageAssets(stage, theme, opts = {}) {
    const { audio = null, onProgress = null } = opts;
    const { textureSlots, audioKeys } = collectStageAssets(stage);

    const tasks = [];
    const total = textureSlots.size + (audio ? audioKeys.size : 0);
    let done = 0;
    const tick = () => { done += 1; onProgress?.(total > 0 ? done / total : 1); };

    for (const slotKey of textureSlots) {
        const p = theme?.preloadTexture?.(slotKey);
        tasks.push(Promise.resolve(p).catch(() => null).then(tick));
    }
    if (audio) {
        for (const key of audioKeys) {
            // Slot key → data URL / http path, then fetch + decode.
            const src = theme?.get?.(key) || null;
            tasks.push(
                (src ? audio.loadBuffer(src) : Promise.resolve(null))
                    .catch(() => null).then(tick),
            );
        }
    }

    if (!tasks.length) { onProgress?.(1); return; }
    await Promise.all(tasks);
}

/**
 * Full-screen "LOADING…" gate with a progress bar, shown while the
 * preloader runs. Pure Pixi (no DOM) so it works in every host.
 *
 *   const gate = createLoadingGate(game, parentContainer);
 *   gate.setProgress(0.4);
 *   gate.destroy();
 */
export function createLoadingGate(game, parent) {
    const root = new Container();
    root.zIndex = 5000;                   // above world, lighting and HUD

    const w = game.width, h = game.height;
    const dim = new Graphics();
    dim.rect(0, 0, w, h);
    dim.fill({ color: 0x0f172a, alpha: 0.92 });
    root.addChild(dim);

    const label = new Text({
        text: 'LOADING…',
        style: new TextStyle({
            fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: 34, fontWeight: '900', fill: '#fbbf24',
            letterSpacing: 4,
        }),
    });
    label.anchor.set(0.5);
    label.x = w / 2;
    label.y = h / 2 - 40;
    root.addChild(label);

    // Progress track + fill.
    const barW = Math.min(520, w * 0.5);
    const barH = 14;
    const barX = (w - barW) / 2;
    const barY = h / 2 + 10;

    const track = new Graphics();
    track.roundRect(barX, barY, barW, barH, barH / 2);
    track.fill({ color: 0xffffff, alpha: 0.12 });
    root.addChild(track);

    const fill = new Graphics();
    root.addChild(fill);

    const setProgress = (frac) => {
        const f = Math.max(0, Math.min(1, frac || 0));
        fill.clear();
        if (f <= 0) return;
        fill.roundRect(barX, barY, Math.max(barH, barW * f), barH, barH / 2);
        fill.fill(0xfbbf24);
    };
    setProgress(0);

    parent.addChild(root);

    return {
        setProgress,
        destroy() {
            try {
                if (root.parent) root.parent.removeChild(root);
                root.destroy({ children: true });
            } catch (_) {}
        },
    };
}
